import { Check, PanelsTopLeft } from "lucide-react";
import { useState } from "react";
import { Button, Card, Modal } from "@/components/common";
import {
  type InterfaceLayout,
  useInterfaceLayout,
} from "@/hooks/useInterfaceLayout";

export function InterfacePreview({ layout }: { layout: InterfaceLayout }) {
  return (
    <div
      className={`interface-preview interface-preview--${layout}`}
      aria-hidden="true"
    >
      <div className="interface-preview-brand">ADARA</div>
      <div className="interface-preview-nav">
        <span />
        <span />
        <span />
        <span />
      </div>
      <div className="interface-preview-body">
        <span className="interface-preview-title" />
        <div className="interface-preview-blocks">
          <span />
          <span />
          <span />
        </div>
        <div className="interface-preview-lines">
          <span />
          <span />
          <span />
        </div>
      </div>
    </div>
  );
}

function ChoiceCards({
  value,
  onSelect,
  disabled,
}: {
  value: InterfaceLayout | null;
  onSelect: (layout: InterfaceLayout) => void;
  disabled?: boolean;
}) {
  return (
    <div className="interface-choices">
      {(["classic", "studio"] as const).map((layout) => (
        <button
          type="button"
          className="interface-choice"
          aria-pressed={value === layout}
          key={layout}
          onClick={() => onSelect(layout)}
          disabled={disabled}
        >
          <InterfacePreview layout={layout} />
          <span className="interface-choice-title">
            {layout === "classic" ? "Stary wygląd" : "Nowy wygląd · Pracownia"}
            {value === layout && <Check size={18} aria-hidden />}
          </span>
          <span className="interface-choice-description">
            {layout === "classic"
              ? "Znany układ z szerokim menu i dotychczasowym pulpitem."
              : "Kompaktowe menu, nowe biurko i generator z podglądem dokumentu."}
          </span>
        </button>
      ))}
    </div>
  );
}

export function InterfaceSetting() {
  const { layout, setLayout, isPending } = useInterfaceLayout();
  const [error, setError] = useState(false);
  return (
    <Card>
      <div className="mb-4 flex items-start gap-3">
        <PanelsTopLeft
          size={22}
          aria-hidden
          className="text-adara-700 dark:text-adara-300"
        />
        <div>
          <h3 className="text-sm font-semibold text-main">Wygląd panelu</h3>
          <p className="mt-1 text-xs text-dim">
            Wybór zapisujemy na Twoim koncie. Działa także po zalogowaniu na
            innym urządzeniu.
          </p>
        </div>
      </div>
      <ChoiceCards
        value={layout}
        disabled={isPending}
        onSelect={async (value) => {
          try {
            await setLayout(value);
            setError(false);
          } catch {
            setError(true);
          }
        }}
      />
      {error && (
        <p role="alert" className="mt-3 text-sm text-red-600">
          Nie udało się zapisać ustawienia. Spróbuj ponownie.
        </p>
      )}
    </Card>
  );
}

export function InterfaceWelcome() {
  const { hasChosen, setLayout, isLoaded, isPending } = useInterfaceLayout();
  const [selection, setSelection] = useState<InterfaceLayout | null>("studio");
  const [dismissed, setDismissed] = useState(false);
  const [error, setError] = useState(false);
  return (
    <Modal
      isOpen={isLoaded && !hasChosen && !dismissed}
      onClose={() => {
        if (!isPending) {
          setDismissed(true);
        }
      }}
      title="Wybierz swój wygląd panelu"
      className="max-w-3xl"
    >
      <p className="mb-5 text-sm text-dim">
        Przygotowaliśmy nowy wygląd Adara. Porównaj oba układy i wybierz
        wygodniejszy dla siebie. Wszystkie funkcje i uprawnienia pozostają
        dostępne.
      </p>
      <ChoiceCards
        value={selection}
        onSelect={setSelection}
        disabled={isPending}
      />
      <p className="mt-4 text-xs text-dim">
        Wybór jest zapisany na Twoim koncie. Zmienisz go w Ustawienia →
        Personalizacja → Wygląd panelu.
      </p>
      {error && (
        <p role="alert" className="mt-3 text-sm text-red-600">
          Nie udało się zapisać wyboru. Spróbuj ponownie.
        </p>
      )}
      <div className="mt-5 flex flex-wrap justify-end gap-3">
        <Button
          variant="ghost"
          disabled={isPending}
          onClick={() => setDismissed(true)}
        >
          Później
        </Button>
        <Button
          disabled={!selection}
          isLoading={isPending}
          onClick={async () => {
            if (selection) {
              try {
                await setLayout(selection);
              } catch {
                setError(true);
              }
            }
          }}
        >
          Zapisz wybór
        </Button>
      </div>
    </Modal>
  );
}
